// Targeted clone helpers to avoid cloning entire large objects when only
// a few nested paths will be mutated. Prefer native structuredClone when
// available and fall back to JSON-based clone.
export function structuredCloneSafe(obj) {
  try {
    if (typeof structuredClone === 'function') return structuredClone(obj)
  } catch {
    // ignore
  }

  try {
    return JSON.parse(JSON.stringify(obj))
  } catch {
    // As a last-resort, return the original reference (caller should avoid mutation)
    return obj
  }
}

/**
 * Create a shallow copy of the provided object but deep-clone (one-level)
 * the commonly mutated array/object paths. This preserves the caller's
 * original object while avoiding the cost of cloning large unrelated fields.
 *
 * options:
 *  - arrays: array of top-level keys that are arrays and whose items may be mutated
 *  - objects: array of top-level keys that are objects and should be cloned
 */
export function targetedCloneForMutation(obj, options = {}) {
  if (!obj || typeof obj !== 'object') return obj

  const arraysToClone = Array.isArray(options.arrays)
    ? options.arrays
    : [
        'classes',
        'features',
        'feats',
        'traits',
        'inventory',
        'items',
        'proficiencies',
        'languages',
        'skills'
      ]

  const objectsToClone = Array.isArray(options.objects)
    ? options.objects
    : ['spells', 'customSpells', 'equipment']

  // Start with a shallow top-level copy
  const out = Array.isArray(obj) ? obj.slice() : { ...obj }

  // Clone array items one-level deep (so modifications to item props won't mutate caller)
  for (const key of arraysToClone) {
    try {
      if (Array.isArray(obj[key])) {
        out[key] = obj[key].map((it) =>
          it && typeof it === 'object' ? structuredCloneSafe(it) : it
        )
      }
    } catch {
      // ignore cloning failures for this key
    }
  }

  // Clone whole objects that may be mutated (e.g., spells keyed by level)
  for (const key of objectsToClone) {
    try {
      if (obj[key] && typeof obj[key] === 'object') {
        out[key] = structuredCloneSafe(obj[key])
      }
    } catch {
      // ignore
    }
  }

  return out
}

export default { structuredCloneSafe, targetedCloneForMutation }
